import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';

/**
 * "Open this entry" — on a finding row, and on each entry in a conflict panel.
 *
 * It comes in the two shapes its two homes need, and the difference is not
 * cosmetic:
 *
 *  • `onClick` alone makes it a BUTTON, which is the point on the Abgleich: the
 *    loaded Crewmeister file lives in that page's state, so it opens the editor
 *    in place — leaving the route would throw the file away.
 *  • `to` makes it a real ANCHOR (the app's rule for anything link-like), which
 *    is the point in the Zeiterfassung: the entry IS a route, and a real href
 *    hands middle-click back to the browser.
 *
 * (A refused save does NOT use this button — see TimeEntryOverlapNotice.)
 */
export interface OpenEntryButtonProps {
    /** Handle the click in place (Abgleich). Ignored when `to` is given. */
    onClick?: () => void;
    /** The entry's route — renders the button as a link to it. */
    to?: string;
}

export default function OpenEntryButton({ onClick, to }: OpenEntryButtonProps) {
    const intl = useIntl();
    const label = intl.formatMessage({ id: 'reconcile.openEntry' });
    return (
        <Tooltip title={label}>
            {to ? (
                <IconButton
                    size="small"
                    aria-label={label}
                    component={RouterLink}
                    to={to}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            ) : (
                <IconButton size="small" aria-label={label} onClick={onClick}>
                    <EditIcon fontSize="small" />
                </IconButton>
            )}
        </Tooltip>
    );
}
